/* Shared, read-only offer evaluation for product pages and both cart surfaces. */
(function (root) {
  'use strict';

  const MAX_QUANTITY = 100;
  const string = (value) => typeof value === 'string' ? value.trim() : '';
  const whole = (value) => Number.isSafeInteger(value) && value >= 0;
  const positive = (value) => Number.isFinite(value) && value > 0;
  const id = (value) => value == null ? '' : String(value);
  const validId = (value) => Number.isSafeInteger(value) && value > 0 || typeof value === 'string' && /^[1-9]\d*$/.test(value);
  const productIds = (values) => new Set((Array.isArray(values) ? values : []).map(id).filter(Boolean));

  function completeCart(cart) {
    if (!whole(cart.item_count) || !whole(cart.total_price) || !Array.isArray(cart.items)) return false;
    if (!cart.items.every((item) => item && validId(item.product_id) && validId(item.variant_id) && whole(item.quantity) && item.quantity > 0)) return false;
    return cart.items.reduce((total, item) => total + item.quantity, 0) === cart.item_count;
  }

  function cents(value) {
    const amount = Math.round(value * 100);
    return positive(value) && Number.isSafeInteger(amount) && amount > 0 ? amount : null;
  }

  function money(value, currency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency,
      minimumFractionDigits: value % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value / 100);
  }

  function active(offer, data, now) {
    if (!string(offer.id)) return false;
    const preview = data.previewInactiveOffers === true;
    // Flow owns native eligibility. Stop advertising stale, removed, or
    // unsupported rules. Liquid permits lifecycle previews only in draft themes.
    const sync = offer.syncState;
    if (!sync || sync.version !== 1 || sync.supported !== true || !['ACTIVE', 'SCHEDULED', 'EXPIRED'].includes(sync.status)) return false;
    if (typeof sync.nativeId !== 'string' || !/^gid:\/\/shopify\/DiscountAutomaticNode\/[1-9]\d*$/.test(sync.nativeId)) return false;
    const checked = typeof sync.checkedAt === 'string' ? Date.parse(sync.checkedAt) : NaN;
    if (!Number.isFinite(checked) || checked > now + 60000) return false;
    if (!preview && (sync.status === 'EXPIRED' || now - checked > 45 * 60000)) return false;
    const currency = string(offer.currency);
    if (!/^[A-Z]{3}$/.test(currency) || currency !== data.cart.currency) return false;
    for (const [field, isStart] of [['startsAt', true], ['endsAt', false]]) {
      if (offer[field] == null || offer[field] === '') continue;
      if (typeof offer[field] !== 'string') return false;
      const date = Date.parse(offer[field]);
      if (!Number.isFinite(date) || (!preview && (isStart ? now < date : now >= date))) return false;
    }
    const countries = Array.isArray(offer.countryCodes) ? offer.countryCodes.map((value) => string(value).toUpperCase()).filter(Boolean) : [];
    return !countries.length || countries.includes(string(data.country).toUpperCase());
  }

  function count(items, eligible) {
    return items.reduce((total, item) => total + (eligible.has(id(item.product_id)) ? item.quantity : 0), 0);
  }

  function titleNoun(value) {
    const matches = string(value).toLowerCase().match(/\b(?:(?:mixing|soup)[\s-]+)?bowls?\b|\b(?:(?:dinner|salad)[\s-]+)?plates?\b|\bmugs?\b|\bcups?\b/g) || [];
    const nouns = new Set(matches.map((match) => match.replace(/[\s-]+/g, ' ').replace(/s$/, '')));
    return nouns.size === 1 ? [...nouns][0] : null;
  }

  function itemWords(offer, eligible) {
    let singular = string(offer.itemSingular);
    const onlyTitles = offer.eligibleProductTitles;
    // A single-product discount uses that product's name, e.g. "Wolf Mug".
    if (!singular && Array.isArray(onlyTitles) && onlyTitles.length === 1 && eligible.size === 1) {
      singular = string(onlyTitles[0]);
    }
    if (singular) {
      // Name chosen above; skip the automatic noun.
    } else if (offer.eligibleProductTitles == null) {
      singular = titleNoun(string(offer.discountTitle) || offer.name);
    } else {
      // Reward products, not qualifying products or a broad shopping collection,
      // decide the noun. A partial, mixed or unknown scope gets neutral wording.
      const titles = offer.eligibleProductTitles;
      const nouns = Array.isArray(titles) && titles.length === eligible.size ? titles.map(titleNoun) : [];
      if (nouns.length && nouns[0] && nouns.every((noun) => noun === nouns[0])) singular = nouns[0];
    }
    singular ||= 'item';
    return { singular, plural: string(offer.itemPlural) || `${singular}s` };
  }

  // A message promising two more pieces must have a valid way to add exactly two,
  // including minimums, multiples, cart quantities and remaining variant stock.
  function canComplete(offer, items, eligible, missing) {
    if (missing === 0) return true;
    const inCart = new Map();
    items.forEach((item) => {
      const variantId = id(item.variant_id);
      inCart.set(variantId, (inCart.get(variantId) || 0) + item.quantity);
    });
    let reachable = new Set([0]);
    const seen = new Set();
    for (const variant of Array.isArray(offer.variants) ? offer.variants : []) {
      if (!variant) continue;
      const variantId = id(variant.id);
      if (!variantId || seen.has(variantId) || !eligible.has(id(variant.productId))) continue;
      seen.add(variantId);
      if (variant.available !== true) continue;
      if (variant.inventoryManagement !== null && variant.inventoryManagement !== 'shopify') continue;
      const minimum = variant.minimum == null ? 1 : variant.minimum;
      const increment = variant.increment == null ? 1 : variant.increment;
      const maximum = variant.maximum == null ? null : variant.maximum;
      if (!whole(minimum) || minimum < 1 || !whole(increment) || increment < 1) continue;
      if (maximum !== null && (!whole(maximum) || maximum < minimum)) continue;
      const already = inCart.get(variantId) || 0;
      let capacity = maximum === null ? missing : Math.min(missing, maximum - already);
      if (variant.inventoryManagement) {
        if (variant.inventoryPolicy !== 'continue') {
          if (!Number.isSafeInteger(variant.inventoryQuantity)) continue;
          capacity = Math.min(capacity, variant.inventoryQuantity - already);
        }
      }
      const additions = [];
      for (let quantity = 1; quantity <= capacity; quantity += 1) {
        const total = already + quantity;
        if (total >= minimum && total % increment === 0) additions.push(quantity);
      }
      const next = new Set(reachable);
      reachable.forEach((quantity) => additions.forEach((addition) => {
        if (quantity + addition <= missing) next.add(quantity + addition);
      }));
      if (next.has(missing)) return true;
      reachable = next;
    }
    return false;
  }

  function link(offer, label) {
    const href = string(offer.collectionUrl);
    // Collection references provide local paths; do not turn editable data into a script URL.
    return /^\/(?!\/)/.test(href) ? { href, label } : null;
  }

  function result(offer, state, message, extra = {}) {
    return {
      id: offer.id,
      state,
      heading: offer.kind === 'shipping' ? 'Free shipping' : 'Complete your set',
      message,
      supportingText: string(offer.supportingText),
      link: null,
      progress: null,
      ...extra,
    };
  }

  function quantityOffer(offer, data, items) {
    const required = offer.requiredQuantity;
    if (!whole(required) || required < 1 || required > MAX_QUANTITY) return null;
    const eligible = productIds(offer.eligibleProductIds);
    if (!eligible.size) return null;
    const qualifiers = productIds(offer.qualifierProductIds);
    const qualifierQuantity = offer.qualifierQuantity == null ? 0 : offer.qualifierQuantity;
    if (!whole(qualifierQuantity) || qualifierQuantity > MAX_QUANTITY) return null;
    if ((qualifierQuantity > 0) !== (qualifiers.size > 0)) return null;
    // Native Buy X Get Y needs separate purchased/rewarded units. Keep paired
    // collections disjoint instead of counting an overlapping item twice.
    if ([...qualifiers].some((productId) => eligible.has(productId))) return null;
    const { singular, plural } = itemWords(offer, eligible);
    const collectionTitle = string(offer.collectionTitle);
    const shopLabel = collectionTitle ? `Shop ${collectionTitle}` : `Shop eligible ${plural}`;
    let reward;
    if (offer.rewardType === 'fixed') {
      const amount = cents(offer.rewardValue);
      if (amount === null) return null;
      reward = `${money(amount, offer.currency)} off`;
    } else if (offer.rewardType === 'percentage' && positive(offer.rewardValue) && offer.rewardValue <= 100) {
      reward = `${offer.rewardValue}% off`;
    } else return null;
    const current = count(items, eligible);
    const qualifierCount = count(items, qualifiers);
    const paired = qualifierQuantity > 0;
    if (data.context === 'product') {
      // Paired offers need separate, explicit qualifying-product copy.
      if (paired || !eligible.has(id(data.productId))) return null;
      if (!canComplete(offer, items, eligible, Math.max(0, required - current))) return null;
      return result(offer, 'hint',    string(offer.hintMessage) || `Choose any ${required} ${required === 1 ? singular : plural} and get ${reward}.`, {
        link: link(offer, shopLabel),
      });
    }
    if (paired ? qualifierCount < qualifierQuantity : current === 0) return null;
    if (current >= required) {
      const title = string(offer.discountTitle);
      const savings = (Array.isArray(data.cart.discounts) ? data.cart.discounts : []).reduce((total, discount) => {
        return total + (title && discount && string(discount.title) === title && whole(discount.amount) ? discount.amount : 0);
      }, 0);
      if (!whole(savings) || savings <= 0) {
        return result(offer, 'qualified', `You have all ${required} ${required === 1 ? singular : plural} in your bag.`, {
          heading: 'Your set is complete',
          supportingText: 'No set discount has been applied yet.',
          progress: { current: required, total: required },
        });
      }
      return result(offer, 'success', `You saved ${money(savings, offer.currency)}.`, {
        heading: 'Your set is complete',
        savingsCents: savings,
        progress: { current: required, total: required },
      });
    }
    const missing = required - current;
    if (!canComplete(offer, items, eligible, missing)) return null;
    return result(offer, 'progress', `Add ${missing} more ${missing === 1 ? singular : plural} and get ${reward}.`, {
      link: link(offer, shopLabel),
      progress: { current, total: required },
    });
  }

  function shippingOffer(offer, data, items) {
    if (data.context !== 'cart' || offer.rewardType !== 'free_shipping') return null;
    const total = data.cart.total_price;
    const threshold = cents(offer.spendThreshold);
    if (threshold === null || !whole(total) || !items.some((item) => item.quantity > 0)) return null;
    const common = {
      supportingText: string(offer.supportingText) || 'Eligibility is confirmed at checkout.',
      progress: { current: Math.min(total, threshold), total: threshold },
    };
    if (total >= threshold) {
      return result(offer, 'shipping-qualified', 'You’ve reached the free shipping minimum.', common);
    }
    return result(offer, 'shipping-progress', `You’re ${money(threshold - total, offer.currency)} away from free shipping.`, {
      ...common,
      link: link(offer, 'Keep shopping'),
    });
  }

  function evaluate(data) {
    if (!data || !data.cart || !['cart', 'product'].includes(data.context)) return null;
    if (data.channel != null && !['quantity', 'shipping'].includes(data.channel)) return null;
    if (data.context === 'cart' && !completeCart(data.cart)) return null;
    const now = data.now == null ? Date.now() : data.now;
    if (!Number.isFinite(now)) return null;
    const items = (Array.isArray(data.cart.items) ? data.cart.items : []).filter((item) => item && whole(item.quantity) && item.quantity > 0);
    const offers = (Array.isArray(data.offers) ? data.offers : []).filter((offer) =>
      offer && (data.channel == null || offer.kind === data.channel) && active(offer, data, now));
    offers.sort((a, b) => {
      const aPriority = Number.isFinite(a.priority) ? a.priority : 100;
      const bPriority = Number.isFinite(b.priority) ? b.priority : 100;
      return aPriority - bPriority || string(a.id).localeCompare(string(b.id));
    });
    const candidates = [];
    for (const offer of offers) {
      const evaluated = offer.kind === 'quantity' ? quantityOffer(offer, data, items) : offer.kind === 'shipping' ? shippingOffer(offer, data, items) : null;
      if (evaluated) candidates.push({ offer, evaluated });
    }
    // A completed set should not disappear behind another set still being built.
    // With no channel, shipping retains its normal priority among the remaining offers.
    const completedQuantity = data.context === 'cart' && candidates.some(({ offer, evaluated }) =>
      offer.kind === 'quantity' && ['success', 'qualified'].includes(evaluated.state));
    return candidates.find(({ offer, evaluated }) =>
      !completedQuantity || offer.kind !== 'quantity' || evaluated.state !== 'progress')?.evaluated || null;
  }

  const api = { evaluate };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.WolfSetOfferEngine = api;
})(typeof window === 'undefined' ? globalThis : window);
